import {
  AdminLayout,
  arrayToDistinct,
  arrayToTree,
  Core,
  Loading,
  SourceInterface,
  useSource,
  useTranslation,
  useUrlParam,
} from "@opendash/core";
import { treeToArray } from "@opendash/core/dist/helper/arrayToTree";
import { Source } from "@opendash/plugin-parse/dist/types-generated";
import { useLocation, useNavigate } from "@opendash/router";
import { AdminToolbar } from "@opendash/ui";
import { Alert, Button, Divider, Empty, Flex, Typography } from "antd";
import { DefaultOptionType } from "antd/es/select";
import { Select } from "antd/lib";
import { observer } from "mobx-react-lite";
import Parse from "parse";
import React, { useEffect, useMemo, useState } from "react";
import MultiSourceScheduleModal from "../../features/schedules/components/SpontaneousScheduleModal";
import { SourceMetaTag } from "../../features/sourcemeta/components/SourceMetaTag";
import { SourceMetaViewer } from "../../features/sourcemeta/components/SourceMetaViewer";

type SourceHierarchy = {
  source: SourceInterface;
  children: SourceHierarchy[];
};

export const SourceMetaFilterPage = observer(() => {
  const t = useTranslation();
  const [currentSource, _, sources, loadingAll] = useSource();
  const [openScheduleModal, setOpenScheduleModal] = useState<boolean>(false);
  const [loadingSelected, setLoadingSelected] = useState<boolean>(false);
  const [selectedSources, setSelectedSources] = useState<Source[]>([]);
  const [initialSearch] = useUrlParam<string>("search", "", "string");
  const [search, setSearch] = useState<string[]>(
    initialSearch ? initialSearch.split(",") : undefined
  );
  const debounce = Core.React.Hooks.useDebounceFunction(handleSearch, 500);
  const nav = useNavigate();
  const loc = useLocation();

  const loading = loadingAll || loadingSelected;

  function handleSearchChange(value: string[]) {
    setSearch(value);
    debounce(value.join(","));
  }

  function handleSearch(value: string) {
    nav(loc.pathname + "?search=" + value);
  }

  // Filter sources based on the currently selected source, so that only the sources that are children of the selected source are displayed
  const filteredSources: SourceInterface[] = useMemo(() => {
    if (sources.length === 0 || !currentSource) return [];
    const hierarchyTree: SourceHierarchy[] = arrayToTree(
      sources.map((source) => ({
        source: source,
        children: [],
      })),
      (obj) => obj.source.parent,
      (obj) => obj.source.id
    );
    const hierarchyFlat = treeToArray(hierarchyTree, (obj) => obj.children);

    const currentSourceHierarchy = hierarchyFlat.find(
      (sh) => sh.source.id === currentSource.id
    );

    return getAllChildSources(currentSourceHierarchy);
  }, [sources, currentSource]);

  // Fetch all sources that are children of the selected source
  useEffect(() => {
    setLoadingSelected(true);
    new Parse.Query(Source)
      .includeAll()
      .limit(1_000_000)
      .containedIn(
        "objectId",
        filteredSources.map((s) => s.id)
      )
      .find()
      .then((foundSources) => {
        setSelectedSources(foundSources);
        setLoadingSelected(false);
      });
  }, [filteredSources]);

  /**
   * Recursively gets all child sources of a source hierarchy
   * @param hierarchy the root source hierarchy
   * @returns all child sources of the hierarchy
   */
  function getAllChildSources(hierarchy: SourceHierarchy): SourceInterface[] {
    const children = hierarchy.children;
    if (!children || children.length <= 0) return [hierarchy.source];

    let allChildren: SourceInterface[] = [hierarchy.source];
    children.forEach((child) => {
      allChildren = allChildren.concat(getAllChildSources(child));
    });

    return allChildren;
  }

  /**
   * Creates DefaultOptionType suggestions for the AutoComplete component
   * @param sources the sources to get the meta values and fields from
   * @param search the search string to filter the suggestions
   * @returns DefaultOptionType[] suggestions for the AutoComplete component (distinct by metaValue + metaField)
   */
  function getMetaSelectOptions(sources: Source[]) {
    const metaMap = getAllMetaMap(sources);
    const suggestions: DefaultOptionType[] = [];
    metaMap.forEach((metaValue, metaField) => {
      metaValue.forEach((value) => {
        suggestions.push({
          value: value ? value.toString() : "",
          label: (
            <Flex>
              <SourceMetaTag metaField={metaField} />
              <Typography.Text>{value}</Typography.Text>
            </Flex>
          ),
          title: metaField,
          key: metaField + value,
        });
      });
    });

    const distinctSuggestions = arrayToDistinct(
      suggestions,
      (suggestion) =>
        suggestion.value.toString().toLowerCase().trim() +
        suggestion.title.toLowerCase().trim(),
      (suggestion) => !!suggestion.value.toString().trim()
    );

    return distinctSuggestions;
  }

  return (
    <AdminLayout>
      <AdminToolbar
        title={t("maintenance:sourcemeta.view.title", {
          source: currentSource.name,
        })}
        description={t("maintenance:sourcemeta.view.description")}
        actions={[
          <Button
            key="create-plan"
            type="primary"
            onClick={() => setOpenScheduleModal(true)}
            disabled={selectedSources.length <= 0}
          >
            {t("maintenance:sourcemeta.view.create_plan.button.label")}
          </Button>,
        ]}
      >
        <Alert
          message={t("maintenance:sourcemeta.view.info.label")}
          description={t("maintenance:sourcemeta.view.info.description")}
          type="info"
          showIcon
        />
        <Divider />
        <Typography.Text strong>
          {t("maintenance:sourcemeta.view.search.label")}
        </Typography.Text>
        <Select
          allowClear
          mode="multiple"
          options={getMetaSelectOptions(selectedSources)}
          style={{ width: "100%" }}
          value={search}
          onChange={(value: string[]) => handleSearchChange(value)}
          placeholder={t("maintenance:sourcemeta.view.search.placeholder")}
        />
      </AdminToolbar>

      {!loading && sources.length > 0 && (
        <SourceMetaViewer sources={selectedSources} />
      )}
      {loading && <Loading message="" />}
      {!loading && sources.length === 0 && <Empty />}

      <MultiSourceScheduleModal
        sources={selectedSources}
        open={openScheduleModal}
        onClose={() => setOpenScheduleModal(false)}
        onSaveSuccess={() => null}
      />
    </AdminLayout>
  );
});

/**
 * Get all meta values and fields from a list of sources
 * @param sources the sources to get the meta values and fields from
 * @returns a Map<string, string[]> where the key is the meta field and the value is an array of meta values found for that key in all sources
 */
function getAllMetaMap(sources: Source[]) {
  const metaMap = new Map<string, string[]>(); // metaField => metaValue
  sources.forEach((source) => {
    if (!source.meta?.data) return;
    Object.keys(source.meta.data).forEach((metaField) => {
      const existingMapValue = metaMap.get(metaField) || [];
      const metaValue = source.meta.data[metaField];
      if (Array.isArray(metaValue)) {
        metaValue.forEach((value) => existingMapValue.push(value));
      } else {
        existingMapValue.push(metaValue);
      }
      metaMap.set(metaField, existingMapValue);
    });
  });
  return metaMap;
}
